"use client";

import * as React from "react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Settings,
  User,
  Plus,
  Home,
  Users,
  LogOut,
  MessageCircle,
  Rocket,
  UserPlus,
  Search,
} from "lucide-react";

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "@/components/ui/command";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";
import { supabase } from "@/lib/supabase";

export function QuickActions({ onCreateSpace, canCreateSpaces = false, open, setOpen }) {
  const navigate = useNavigate();
  const { user, userProfile, company } = useAuth();
  const permissions = usePermissions(['spaces.viewAll', 'users.invite']);
  const [spaces, setSpaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState({ users: [], spaces: [] });
  const [searchLoading, setSearchLoading] = useState(false);

  // Load user's spaces
  useEffect(() => {
    const loadSpaces = async () => {
      if (!company?.id || !userProfile?.id) {
        setLoading(false);
        return;
      }

      try {
        let query = supabase
          .from('projects')
          .select('*')
          .eq('company_id', company.id)
          .eq('is_archived', false)
          .order('name');

        // If user can't view all spaces, only show spaces they're a member of
        if (!permissions['spaces.viewAll']) {
          const { data: spaceMemberships } = await supabase
            .from('space_members')
            .select('project_id')
            .eq('user_id', userProfile.id);

          if (spaceMemberships && spaceMemberships.length > 0) {
            const projectIds = spaceMemberships.map(sm => sm.project_id);
            query = query.in('id', projectIds);
          } else {
            setSpaces([]);
            setLoading(false);
            return;
          }
        }

        const { data, error } = await query;

        if (error) {
          console.error('Error loading spaces:', error);
        } else {
          setSpaces(data || []);
        }
      } catch (error) {
        console.error('Error loading spaces:', error);
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      loadSpaces();
      // Reset search when dialog opens
      setSearchQuery("");
      setSearchResults({ users: [], spaces: [] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, company?.id, userProfile?.id]);

  // Search functionality for users only (spaces are already shown below)
  useEffect(() => {
    const searchData = async () => {
      if (!searchQuery || searchQuery.length < 2 || !company?.id) {
        setSearchResults({ users: [], spaces: [] });
        return;
      }

      setSearchLoading(true);
      try {
        console.log('Searching for users with query:', searchQuery, 'in company:', company.id);
        
        // First get all user IDs in the same company from company_members
        const { data: companyMembers, error: membersError } = await supabase
          .from('company_members')
          .select('user_id')
          .eq('company_id', company.id);

        console.log('Company members result:', companyMembers, 'Error:', membersError);

        if (membersError) {
          console.error('Error fetching company members:', membersError);
          setSearchResults({ users: [], spaces: [] });
          setSearchLoading(false);
          return;
        }

        if (!companyMembers || companyMembers.length === 0) {
          console.log('No company members found');
          setSearchResults({ users: [], spaces: [] });
          setSearchLoading(false);
          return;
        }

        // Extract user IDs
        const userIds = companyMembers.map(m => m.user_id);
        console.log('User IDs to search:', userIds);

        // Search users in those IDs - include display_name in search
        const { data: users, error: searchError } = await supabase
          .from('user_profiles')
          .select('*')
          .in('id', userIds)
          .or(`first_name.ilike.%${searchQuery}%,last_name.ilike.%${searchQuery}%,display_name.ilike.%${searchQuery}%,email.ilike.%${searchQuery}%`)
          .limit(10);

        console.log('User search result:', users, 'Error:', searchError);

        if (searchError) {
          console.error('Error searching users:', searchError);
        }

        setSearchResults({
          users: users || [],
          spaces: [] // Don't filter spaces here, let Command component handle it
        });
      } catch (error) {
        console.error('Error searching:', error);
      } finally {
        setSearchLoading(false);
      }
    };

    const debounceTimer = setTimeout(searchData, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery, company?.id]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/login");
    setOpen(false);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setOpen(false);
  };

  const handleCreateSpace = () => {
    setOpen(false);
    onCreateSpace?.();
  };

  const handleSpaceNavigation = (spaceId) => {
    navigate(`/space/${spaceId}`);
    setOpen(false);
  };

  const handleUserNavigation = (userId) => {
    navigate(`/profile/${userId}`);
    setOpen(false);
  };

  const getUserInitials = (profile) => {
    if (profile?.first_name && profile?.last_name) {
      return `${profile.first_name.charAt(0)}${profile.last_name.charAt(0)}`.toUpperCase();
    }
    if (profile?.first_name) {
      return profile.first_name.substring(0, 2).toUpperCase();
    }
    if (profile?.email) {
      return profile.email.substring(0, 2).toUpperCase();
    }
    return 'US';
  };

  const getUserDisplayName = (profile) => {
    // Prioritize display_name if available
    if (profile?.display_name) {
      return profile.display_name;
    }
    if (profile?.first_name && profile?.last_name) {
      return `${profile.first_name} ${profile.last_name}`;
    }
    if (profile?.first_name || profile?.last_name) {
      return profile.first_name || profile.last_name;
    }
    return profile?.email?.split('@')[0] || 'User';
  };

  // Filter static items when searching
  const query = searchQuery.toLowerCase().trim();
  const isSearching = query.length >= 2;

  const filteredSpaces = isSearching
    ? spaces.filter(s => s.name?.toLowerCase().includes(query) || s.type?.toLowerCase().includes(query))
    : spaces;

  const staticItems = [
    { label: "Invite People", group: "actions" },
    { label: "Create New Space", group: "actions" },
    { label: "Home", group: "nav" },
    { label: "Profile", group: "nav" },
    { label: "Chat", group: "nav" },
    { label: "Team", group: "nav" },
    { label: "Search", group: "nav" },
    { label: "Settings", group: "account" },
    { label: "Log Out", group: "account" },
  ];

  const matchesQuery = (label) => !isSearching || label.toLowerCase().includes(query);

  const showActions = matchesQuery("Invite People") || matchesQuery("Create New Space");
  const showNav = staticItems.filter(i => i.group === "nav" && matchesQuery(i.label)).length > 0;
  const showAccount = matchesQuery("Settings") || matchesQuery("Log Out");

  const hasAnyResults =
    (isSearching && searchResults.users.length > 0) ||
    filteredSpaces.length > 0 ||
    showActions || showNav || showAccount;

  return (
    <CommandDialog open={open} onOpenChange={setOpen} shouldFilter={false}>
      <CommandInput
        placeholder="Type a command or search..."
        value={searchQuery}
        onValueChange={setSearchQuery}
      />
      <CommandList>
        {isSearching && !hasAnyResults && !searchLoading && (
          <CommandEmpty>No results found.</CommandEmpty>
        )}

        {/* Show search results for users when typing */}
        {isSearching && searchResults.users.length > 0 && (
          <>
            <CommandGroup heading="People">
              {searchResults.users.map((profile) => (
                <CommandItem
                  key={`user-${profile.id}`}
                  value={`${getUserDisplayName(profile)} ${profile.email}`}
                  onSelect={() => handleUserNavigation(profile.id)}
                >
                  <User className="mr-2 h-4 w-4" />
                  <div className="flex items-center gap-2 flex-1">
                    <span>{getUserDisplayName(profile)}</span>
                    <span className="text-xs text-muted-foreground">{profile.email}</span>
                  </div>
                  {profile.job_title && (
                    <Badge variant="outline" className="ml-auto">
                      {profile.job_title}
                    </Badge>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {/* Quick Actions */}
        {showActions && (
          <>
            <CommandGroup heading="Quick Actions">
              {matchesQuery("Invite People") && (
                <CommandItem
                  onSelect={() => permissions['users.invite'] && handleNavigation("/company-team")}
                  disabled={!permissions['users.invite']}
                >
                  <UserPlus className="mr-2 h-4 w-4" />
                  <span>Invite People</span>
                  {!permissions['users.invite'] && (
                    <Badge variant="outline" className="ml-auto text-xs">No permission</Badge>
                  )}
                  {permissions['users.invite'] && <CommandShortcut>⌘I</CommandShortcut>}
                </CommandItem>
              )}
              {matchesQuery("Create New Space") && (
                <CommandItem
                  onSelect={() => canCreateSpaces && handleCreateSpace()}
                  disabled={!canCreateSpaces}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  <span>Create New Space</span>
                  {!canCreateSpaces && (
                    <Badge variant="outline" className="ml-auto text-xs">No permission</Badge>
                  )}
                  {canCreateSpaces && <CommandShortcut>⌘N</CommandShortcut>}
                </CommandItem>
              )}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {/* User's Spaces */}
        {(filteredSpaces.length > 0 || (!isSearching && loading)) && (
          <>
            <CommandGroup heading="Your Spaces">
              {loading ? (
                <CommandItem disabled>
                  <div className="animate-pulse">Loading spaces...</div>
                </CommandItem>
              ) : (
                filteredSpaces.map((space) => (
                  <CommandItem
                    key={space.id}
                    value={space.name}
                    onSelect={() => handleSpaceNavigation(space.id)}
                  >
                    <Rocket
                      className="mr-2 h-4 w-4"
                      style={{ color: space.color || '#3b82f6' }}
                    />
                    <span>{space.name}</span>
                    {space.type && (
                      <Badge variant="outline" className="ml-auto">
                        {space.type}
                      </Badge>
                    )}
                  </CommandItem>
                ))
              )}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {/* Navigation */}
        {showNav && (
          <>
            <CommandGroup heading="Navigation">
              {matchesQuery("Home") && (
                <CommandItem onSelect={() => handleNavigation("/dashboard")}>
                  <Home className="mr-2 h-4 w-4" />
                  <span>Home</span>
                </CommandItem>
              )}
              {matchesQuery("Profile") && (
                <CommandItem onSelect={() => handleNavigation("/profile")}>
                  <User className="mr-2 h-4 w-4" />
                  <span>Profile</span>
                </CommandItem>
              )}
              {matchesQuery("Chat") && (
                <CommandItem onSelect={() => handleNavigation("/chat")}>
                  <MessageCircle className="mr-2 h-4 w-4" />
                  <span>Chat</span>
                </CommandItem>
              )}
              {matchesQuery("Team") && (
                <CommandItem onSelect={() => handleNavigation("/company-team")}>
                  <Users className="mr-2 h-4 w-4" />
                  <span>Team</span>
                </CommandItem>
              )}
              {matchesQuery("Search") && (
                <CommandItem onSelect={() => setOpen(false)}>
                  <Search className="mr-2 h-4 w-4" />
                  <span>Search</span>
                  <CommandShortcut>⌘K</CommandShortcut>
                </CommandItem>
              )}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {/* Settings & Account */}
        {showAccount && (
          <CommandGroup heading="Account">
            {matchesQuery("Settings") && (
              <CommandItem onSelect={() => handleNavigation("/settings")}>
                <Settings className="mr-2 h-4 w-4" />
                <span>Settings</span>
                <CommandShortcut>⌘,</CommandShortcut>
              </CommandItem>
            )}
            {matchesQuery("Log Out") && (
              <CommandItem onSelect={handleLogout}>
                <LogOut className="mr-2 h-4 w-4" />
                <span>Log Out</span>
                <CommandShortcut>⌘Q</CommandShortcut>
              </CommandItem>
            )}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
}